import { useState } from "react";
import { api, useApi, ApiError } from "../lib/api";
import { NeedsRole, useGrant } from "../lib/grant";

/** The one route this panel writes (PUT) and reads (GET); the role table keys on it. */
export const OBS_CONFIG_PATH = "/v1/observability/config";
const OBS_HEALTH_PATH = "/v1/observability/health";

interface ConfigMeta { backend: string; url: string | null; datasourceUid: string | null; tokenLast4: string; updatedAt: string }
interface Health { adapter: string; live: boolean; health: { reachable: boolean; reason: string | null } }

const inputStyle: React.CSSProperties = {
  padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)",
};

/**
 * The tenant's observability backend: which adapter the hub is on and whether it is live (read by
 * everyone), and — for ADMIN only — the form that points this tenant at its Grafana. The token is
 * typed once into a password field and cleared on save; the API never returns it, only its last
 * four. The provider is an operator-level choice made at boot (OBSERVABILITY_GRAFANA_URL), so the
 * form can only configure Grafana: "mock" is shown as what the operator default is, not a savable value.
 */
export function ObservabilityProvider() {
  const cfg = useApi<{ config: ConfigMeta | null }>(OBS_CONFIG_PATH);
  const health = useApi<Health>(OBS_HEALTH_PATH);
  const grant = useGrant(OBS_CONFIG_PATH, "PUT");
  const [url, setUrl] = useState("");
  const [datasourceUid, setDatasourceUid] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    setSaved(false);
    try {
      const body: Record<string, string> = { token };
      if (url.trim()) body["url"] = url.trim();
      if (datasourceUid.trim()) body["datasourceUid"] = datasourceUid.trim();
      await api(OBS_CONFIG_PATH, { method: "PUT", body: JSON.stringify(body) });
      setToken("");
      setSaved(true);
      cfg.reload();
      health.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? `${err.message}${err.hint ? ` — ${err.hint}` : ""}` : String(err));
    } finally {
      setBusy(false);
    }
  }

  const h = health.data;
  const c = cfg.data?.config ?? null;
  return (
    <div className="panel" data-testid="obs-provider">
      <h2>Provider</h2>
      {h && (
        <p data-testid="obs-liveness" style={{ marginTop: 8 }}>
          <span className={`pill ${h.live ? "s-ok" : "s-warn"}`}>{h.live ? "live" : "not live"}</span>{" "}
          <span className="muted" data-testid="obs-adapter">{h.adapter}</span>
        </p>
      )}
      {h && !h.live && (
        <p data-testid="mock-banner" style={{ marginTop: 8, color: "var(--warn)" }}>
          Not live{h.health.reason ? `: ${h.health.reason}` : ""}. Nothing on this screen is a product's telemetry until a backend answers.
        </p>
      )}
      {health.error && <p className="err">{health.error.message}{health.error.hint ? ` — ${health.error.hint}` : ""}</p>}
      <p className="muted" data-testid="obs-config" style={{ marginTop: 8 }}>
        {c
          ? `This tenant's backend: ${c.backend}${c.url ? ` at ${c.url}` : ""}${c.datasourceUid ? `, datasource ${c.datasourceUid}` : ""}, token ending ${c.tokenLast4}.`
          : "This tenant has no backend configured."}
      </p>
      {grant.allowed ? (
        <form onSubmit={save} data-testid="write-form" data-path={OBS_CONFIG_PATH} style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <select aria-label="provider" value="grafana" onChange={() => undefined} style={inputStyle}>
            <option value="grafana">grafana</option>
            <option value="mock" disabled>mock (operator default, not savable)</option>
          </select>
          <input aria-label="base URL" placeholder="base URL (optional)" type="url" value={url}
            onChange={(e) => setUrl(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          <input aria-label="datasource uid" placeholder="datasource uid" value={datasourceUid}
            onChange={(e) => setDatasourceUid(e.target.value)} style={{ ...inputStyle, width: 160 }} />
          <input aria-label="token" placeholder="token" type="password" autoComplete="off" required value={token}
            onChange={(e) => setToken(e.target.value)} style={{ ...inputStyle, width: 200 }} />
          <button disabled={busy} type="submit"
            style={{ padding: "8px 16px", background: "var(--accent)", border: 0, borderRadius: 6, color: "#fff", cursor: "pointer" }}>
            {busy ? "…" : "Save"}
          </button>
        </form>
      ) : <NeedsRole path={OBS_CONFIG_PATH} method="PUT" />}
      {saved && <p className="muted" data-testid="obs-saved" style={{ marginTop: 8 }}>Saved. The token is not shown again.</p>}
      {problem && <p className="err" style={{ marginTop: 8 }}>{problem}</p>}
    </div>
  );
}
